import React, { useState, useEffect } from 'react';
import { Ban, Trash2, Search, Shield, UserCheck, Edit, MessageSquare, Unlock } from 'lucide-react';
import { AdminLayout, AdminTable, AdminPagination, ConfirmationModal, UserProfileModal } from '../../components/admin';
import { Button, Input, ProfileAvatar } from '../../components/ui';
import { UserMessageModal } from '../../components/admin/UserMessageModal';
import { toast } from '../../components/ui/Toaster';
import { adminUserService } from '../../services/adminUserService';

interface UserData {
  id: number;
  username: string;
  email: string;
  emailVerified?: boolean;
  profilePhoto: string | null;
  birthdate: string;
  gender: string;
  bio: string | null;
  isAdmin: boolean;
  isSuspended: boolean;
  suspendedAt: string | null;
  lockedUntil?: string | null;
  failedLoginAttempts?: number;
  createdAt: string;
  updatedAt: string;
  _count: {
    ownedGroups: number;
    groupMemberships: number;
    messages: number;
    createdEvents: number;
  };
}

interface UsersResponse {
  users: UserData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [search, setSearch] = useState('');
  const [suspendedFilter, setSuspendedFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Modals
  const [suspendModal, setSuspendModal] = useState<{
    isOpen: boolean;
    user: UserData | null;
    suspend: boolean;
    loading: boolean;
  }>({
    isOpen: false,
    user: null,
    suspend: false,
    loading: false
  });
  
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    user: UserData | null;
    loading: boolean;
  }>({
    isOpen: false,
    user: null,
    loading: false
  });

  const [messageModal, setMessageModal] = useState<{userId: string, username: string} | null>(null);
  
  // User Profile Modal
  const [profileModal, setProfileModal] = useState<{
    isOpen: boolean;
    userId: string;
  }>({
    isOpen: false,
    userId: ''
  });

  useEffect(() => {
    loadUsers();
  }, [pagination.page, pagination.limit, search, suspendedFilter, adminFilter, sortBy, sortOrder]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder
      };
      
      if (search) params.search = search;
      if (suspendedFilter) params.suspended = suspendedFilter === 'true';
      if (adminFilter) params.isAdmin = adminFilter === 'true';
      
      const response: UsersResponse = await adminUserService.getAllUsers(params);
      
      setUsers(response.users);
      setPagination(response.pagination);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      toast.error('Fout', 'Kon gebruikers niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleSuspendUser = async () => {
    if (!suspendModal.user) return;
    
    try {
      setSuspendModal(prev => ({ ...prev, loading: true }));
      
      await adminUserService.suspendUser(suspendModal.user.id, suspendModal.suspend);
      
      toast.success('Succes', `Gebruiker ${suspendModal.suspend ? 'geschorst' : 'schorsing opgeheven'} succesvol`);
      setSuspendModal({ isOpen: false, user: null, suspend: false, loading: false });
      
      // Reload users
      loadUsers();
    } catch (error: any) {
      console.error('Failed to change user suspension status:', error);
      toast.error('Fout', error.message || 'Kon gebruiker schorsingsstatus niet wijzigen');
      setSuspendModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.user) return;
    
    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));
      
      await adminUserService.deleteUser(deleteModal.user.id);
      
      toast.success('Succes', 'Gebruiker succesvol verwijderd');
      setDeleteModal({ isOpen: false, user: null, loading: false });
      
      // Reload users
      loadUsers();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast.error('Fout', error.message || 'Kon gebruiker niet verwijderen');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleUnlockUser = async (userId: number) => {
    try {
      await adminUserService.unlockUser(userId);
      toast.success('Account ontgrendeld', 'Gebruiker kan nu weer inloggen');
      
      // Refresh the users list to show updated status
      const response = await adminUserService.getAllUsers({
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        search: search,
        ...(suspendedFilter === 'true' && { suspended: true }),
        ...(suspendedFilter === 'false' && { suspended: false }),
        ...(adminFilter === 'true' && { isAdmin: true }),
        ...(adminFilter === 'false' && { isAdmin: false }),
      });
      setUsers(response.users);
    } catch (error: any) {
      console.error('Failed to unlock user:', error);
      toast.error('Fout', error.message || 'Kon gebruiker niet ontgrendelen');
    }
  };

  const columns = [
    {
      key: 'user',
      title: 'Gebruiker',
      render: (_: any, user: UserData) => (
        <div className="flex items-center gap-2.5">
          <ProfileAvatar
            user={user}
            size="sm"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium text-fg-bright">{user.username}</span>
              {user.isAdmin && (
                <Shield className="h-3.5 w-3.5 shrink-0 text-neon-cyan" />
              )}
            </div>
            <div className="truncate text-xs text-fg-muted">{user.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      title: 'Status',
      render: (_: any, user: UserData) => {
        const isLocked = user.lockedUntil && new Date(user.lockedUntil) > new Date();
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {user.isSuspended ? (
              <span className="sf-badge-pink">Geschorst</span>
            ) : isLocked ? (
              <span
                className="sf-badge-pink"
                title={`Vergrendeld tot ${new Date(user.lockedUntil!).toLocaleString('nl-NL')}`}
              >
                🔒 Vergrendeld
              </span>
            ) : (
              <span className="sf-badge-lime">Actief</span>
            )}
            {!user.emailVerified && (
              <span className="sf-badge-cyan" title="E-mail nog niet geverifieerd">! E-mail</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      title: 'Aangemaakt',
      sortable: true,
      render: (value: string) => (
        <span className="whitespace-nowrap text-xs text-fg-body">
          {new Date(value).toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
        </span>
      )
    },
    {
      key: 'messages',
      title: 'Berichten',
      render: (_: any, user: UserData) => (
        <span className="text-sm font-medium text-fg-body">
          {user._count.messages || 0}
        </span>
      )
    }
  ];

  const renderActions = (user: UserData) => (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={() => setProfileModal({
          isOpen: true,
          userId: user.id.toString()
        })}
        className="rounded-lg p-1.5 text-neon-cyan transition-colors hover:bg-neon-cyan/10"
        title="Bewerk profiel"
      >
        <Edit className="h-4 w-4" />
      </button>
      <button
        onClick={() => setMessageModal({
          userId: user.id.toString(),
          username: user.username
        })}
        className="rounded-lg p-1.5 text-neon-violet transition-colors hover:bg-neon-violet/10"
        title="Bekijk berichten"
      >
        <MessageSquare className="h-4 w-4" />
      </button>
      <button
        onClick={() => setSuspendModal({
          isOpen: true,
          user,
          suspend: !user.isSuspended,
          loading: false
        })}
        className={`rounded-lg p-1.5 transition-colors ${user.isSuspended ? 'text-neon-lime hover:bg-neon-lime/10' : 'text-warning-500 hover:bg-warning-500/10'}`}
        title={user.isSuspended ? 'Schorsing opheffen' : 'Gebruiker schorsen'}
      >
        {user.isSuspended ? <UserCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
      </button>
      {/* Unlock Button - only show for locked accounts */}
      {user.lockedUntil && new Date(user.lockedUntil) > new Date() && (
        <button
          onClick={() => handleUnlockUser(user.id)}
          className="rounded-lg p-1.5 text-neon-cyan transition-colors hover:bg-neon-cyan/10"
          title="Account ontgrendelen"
        >
          <Unlock className="h-4 w-4" />
        </button>
      )}
      <button
        onClick={() => setDeleteModal({ isOpen: true, user, loading: false })}
        className="rounded-lg p-1.5 text-neon-pink-soft transition-colors hover:bg-neon-pink/10"
        title="Gebruiker verwijderen"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <AdminLayout 
      title="Gebruiker beheer" 
      subtitle="Beheer gebruikersaccounts en rechten"
    >
      {/* Filters */}
      <div className="sf-card p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-fg-faint" />
            <Input
              type="text"
              placeholder="Zoek gebruikers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={suspendedFilter}
            onChange={(e) => setSuspendedFilter(e.target.value)}
            className="sf-input py-2.5 text-sm"
          >
            <option value="">Alle gebruikers</option>
            <option value="false">Alleen actief</option>
            <option value="true">Alleen geschorst</option>
          </select>

          <select
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value)}
            className="sf-input py-2.5 text-sm"
          >
            <option value="">Alle rollen</option>
            <option value="false">Gewone gebruikers</option>
            <option value="true">Alleen admins</option>
          </select>

          <Button
            variant="outline"
            onClick={() => {
              setSearch('');
              setSuspendedFilter('');
              setAdminFilter('');
            }}
          >
            Filters wissen
          </Button>
        </div>
      </div>

      {/* Users Table */}
      <AdminTable
        columns={columns}
        data={users}
        loading={loading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        actions={renderActions}
        emptyMessage="Geen gebruikers gevonden"
      />

      {/* Pagination */}
      {!loading && users.length > 0 && (
        <div className="mt-6">
          <AdminPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </div>
      )}

      {/* Suspend/Unsuspend Confirmation Modal */}
      <ConfirmationModal
        isOpen={suspendModal.isOpen}
        onClose={() => !suspendModal.loading && setSuspendModal({ isOpen: false, user: null, suspend: false, loading: false })}
        onConfirm={handleSuspendUser}
        title={suspendModal.suspend ? "Gebruiker schorsen" : "Schorsing opheffen"}
        message={`Weet je zeker dat je "${suspendModal.user?.username}" wilt ${suspendModal.suspend ? 'schorsen' : 'de schorsing wilt opheffen'}? ${suspendModal.suspend ? 'Dit voorkomt dat ze kunnen inloggen.' : 'Dit zorgt ervoor dat ze weer kunnen inloggen.'}`}
        confirmText={suspendModal.suspend ? "Gebruiker schorsen" : "Schorsing opheffen"}
        type={suspendModal.suspend ? "warning" : "info"}
        loading={suspendModal.loading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => !deleteModal.loading && setDeleteModal({ isOpen: false, user: null, loading: false })}
        onConfirm={handleDeleteUser}
        title="Gebruiker verwijderen"
        message={`Weet je zeker dat je "${deleteModal.user?.username}" wilt verwijderen? Dit verwijdert permanent hun account en alle bijbehorende data (groepen, berichten, evenementen). Deze actie kan niet ongedaan worden gemaakt.`}
        confirmText="Gebruiker verwijderen"
        type="danger"
        loading={deleteModal.loading}
      />

      {/* User Message Modal */}
      {messageModal && (
        <UserMessageModal
          userId={messageModal.userId}
          username={messageModal.username}
          isOpen={!!messageModal}
          onClose={() => setMessageModal(null)}
        />
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={profileModal.isOpen}
        onClose={() => setProfileModal({ isOpen: false, userId: '' })}
        userId={profileModal.userId}
        onUserUpdated={loadUsers}
      />
    </AdminLayout>
  );
};

export default UserManagementPage;